import { memo } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { NumberInvalidIcon, SmsBlockedIcon } from '#/shared/ui/icons'
import {
  TooltipContent,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
} from '#/shared/ui/tooltip'

import { getCustomerHealthBadge, getHealthBadge } from '../lib/derive'
import type { CustomerDeliverability, HealthBadge } from '../lib/derive'
import { useNumberHealth } from '../model/number-health-context'
import {
  NumberHealthTooltip,
  NumberHealthTooltipHeader,
} from './number-health-tooltip'

const TONE_CLASS = {
  warn: 'text-orange-700',
  bad: 'text-red-500',
} as const

const TONE_ICON = {
  warn: SmsBlockedIcon,
  bad: NumberInvalidIcon,
} as const

/**
 * The badge glyph plus its hover card. The trigger is a span (not a button) so
 * the badge stays valid HTML inside interactive rows — e.g. the conversation
 * list's selection rows, which are themselves buttons.
 */
function BadgeShell({
  badge,
  className,
  children,
}: {
  badge: HealthBadge
  className?: string
  children: React.ReactNode
}) {
  const { t } = useTranslation()
  const Icon = TONE_ICON[badge.tone]
  return (
    <TooltipProvider delayDuration={200}>
      <TooltipRoot>
        <TooltipTrigger asChild>
          <span
            role="img"
            onClick={(event) => event.stopPropagation()}
            aria-label={t(badge.labelKey)}
            className={cn(
              'inline-flex shrink-0 cursor-default items-center',
              TONE_CLASS[badge.tone],
              className,
            )}
          >
            <Icon className="size-3.5" />
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="start"
          sideOffset={8}
          showArrow={false}
          className="max-w-none rounded-xl border border-warm-100 bg-paper p-4 text-left font-normal text-gray-900 shadow-popover"
        >
          {children}
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  )
}

/**
 * Deliverability badge shown beside a phone number, driven by the batched
 * number-health lookup. Fallback path only — use it when there is no resolved
 * customer (unknown number); a known customer carries its deliverability
 * inline, rendered by {@link CustomerSmsBadge}. Renders nothing for a healthy
 * (or not-yet-scored) number.
 */
function NumberHealthBadgeImpl({
  phone,
  className,
}: {
  phone?: string | null
  className?: string
}) {
  const health = useNumberHealth(phone)
  const badge = getHealthBadge(health)
  if (!health || !badge) return null

  return (
    <BadgeShell badge={badge} className={className}>
      <NumberHealthTooltip health={health} />
    </BadgeShell>
  )
}

export const NumberHealthBadge = memo(NumberHealthBadgeImpl)

/** The lite hover card for an inline badge: the customer record carries only
 *  deliverable + reason, so there's no carrier/line detail to tabulate — just
 *  the shared status header, so it reads like a trimmed full card. */
function InlineTooltip({ badge }: { badge: HealthBadge }) {
  const { t } = useTranslation()
  const invalid = badge.tone === 'bad'
  return (
    <div className="w-60 text-xs">
      <NumberHealthTooltipHeader
        tone={invalid ? 'bad' : 'warn'}
        titleKey={
          invalid
            ? 'numberHealth.tooltip.title.invalid'
            : 'numberHealth.tooltip.title.landline'
        }
        summary={t(
          invalid
            ? 'numberHealth.tooltip.hint.invalid'
            : 'numberHealth.tooltip.hint.landline',
        )}
      />
    </div>
  )
}

/**
 * Deliverability badge for a known customer, rendered straight from the
 * inline `sms_deliverable` / `deliverability_reason` fields on the customer
 * object — no number-health call. Renders nothing while the number is
 * deliverable (or the fields haven't been backfilled yet).
 */
function CustomerSmsBadgeImpl({
  customer,
  className,
}: {
  customer: CustomerDeliverability | null | undefined
  className?: string
}) {
  const badge = getCustomerHealthBadge(customer)
  if (!badge) return null
  return (
    <BadgeShell badge={badge} className={className}>
      <InlineTooltip badge={badge} />
    </BadgeShell>
  )
}

export const CustomerSmsBadge = memo(CustomerSmsBadgeImpl)
